/**
 * Supabase API 客戶端
 * 
 * 用於從 Supabase 獲取結構化字典數據
 * 替換原有的 Sanity API
 */

// 環境變數配置
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || process.env.SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY;

/**
 * 檢查 Supabase 是否已配置
 */
const isConfigured = () => {
  return !!(SUPABASE_URL && SUPABASE_ANON_KEY);
};

/**
 * 構建 Supabase REST API 請求
 */
const supabaseRequest = async (endpoint, options = {}) => {
  if (!isConfigured()) {
    console.error('[Supabase API] 配置檢查失敗', {
      url: SUPABASE_URL ? '已設置' : '未設置',
      key: SUPABASE_ANON_KEY ? '已設置' : '未設置'
    });
    throw new Error('Supabase not configured');
  }

  // 確保 URL 格式正確（移除末尾斜杠，避免雙斜杠）
  const baseUrl = SUPABASE_URL.replace(/\/$/, '');
  const url = `${baseUrl}/rest/v1/${endpoint}`;
  
  const headers = {
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
    'Prefer': 'return=representation',
    ...options.headers
  };

  // 開發環境下記錄請求詳情
  if (process.env.NODE_ENV === 'development') {
    console.log('[Supabase API] 發送請求', {
      url,
      endpoint,
      method: options.method || 'GET',
      hasHeaders: !!headers
    });
  }

  let response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      mode: 'cors', // 明確指定 CORS 模式
      credentials: 'omit' // 不發送憑證
    });
  } catch (fetchError) {
    // 處理網絡錯誤（如 CORS、網絡連接失敗等）
    const errorMessage = fetchError.message || 'Unknown error';
    console.error('[Supabase API] 網絡請求失敗', {
      url,
      error: errorMessage,
      type: fetchError.name,
      stack: fetchError.stack
    });
    
    if (errorMessage === 'Failed to fetch' || errorMessage.includes('fetch')) {
      // 提供更詳細的錯誤信息
      const detailedError = `網絡連接失敗：無法連接到 Supabase 服務器。
可能的原因：
1. 網絡連接問題
2. CORS 配置問題
3. Supabase URL 配置錯誤：${SUPABASE_URL}
4. Supabase 服務器不可用

請檢查：
- 網絡連接是否正常
- Supabase Dashboard 中項目的狀態
- .env 文件中的 REACT_APP_SUPABASE_URL 是否正確`;
      throw new Error(detailedError);
    }
    throw fetchError;
  }

  if (!response.ok) {
    let errorText;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = '無法讀取錯誤響應';
    }
    
    console.error('[Supabase API] HTTP 錯誤', {
      url,
      status: response.status,
      statusText: response.statusText,
      error: errorText
    });
    
    throw new Error(`HTTP 錯誤！狀態碼: ${response.status}, 消息: ${errorText}`);
  }

  try {
    const data = await response.json();
    if (process.env.NODE_ENV === 'development') {
      console.log('[Supabase API] 請求成功', {
        url,
        dataLength: Array.isArray(data) ? data.length : Object.keys(data || {}).length
      });
    }
    return data;
  } catch (parseError) {
    console.error('[Supabase API] JSON 解析失敗', {
      url,
      error: parseError.message
    });
    throw new Error(`響應解析失敗：${parseError.message}`);
  }
};

/**
 * 獲取字典版本號
 * 這是一個輕量級請求，用於檢查是否有更新
 */
export const getDictionaryVersion = async () => {
  if (!isConfigured()) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Supabase API] Supabase not configured. Using local dictionaries.');
      console.warn('[Supabase API] 配置狀態:', {
        url: SUPABASE_URL || '未設置',
        key: SUPABASE_ANON_KEY ? `${SUPABASE_ANON_KEY.substring(0, 10)}...` : '未設置'
      });
    }
    return null;
  }

  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Supabase API] 開始獲取字典版本號...');
    }
    
    // 先嘗試獲取包含 version_md5 的版本號
    let result;
    
    try {
      result = await supabaseRequest(
        'dictionary_versions?select=version,version_md5,updated_at&order=updated_at.desc&limit=1'
      );
      if (process.env.NODE_ENV === 'development') {
        console.log('[Supabase API] 成功獲取版本號（包含 version_md5）');
      }
    } catch (error) {
      // 檢查是否是因為 version_md5 欄位不存在
      const errorMessage = error.message || '';
      const errorString = JSON.stringify(error);
      
      // 檢查錯誤訊息中是否包含 version_md5 不存在的提示
      const isVersionMD5Error = 
        errorMessage.includes('version_md5') || 
        errorMessage.includes('42703') || // PostgreSQL 錯誤代碼：未定義的欄位
        errorString.includes('version_md5') ||
        (errorMessage.includes('does not exist') && errorMessage.includes('column'));
      
      if (isVersionMD5Error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Supabase API] version_md5 欄位不存在，使用基本版本查詢');
        }
        // 如果查詢失敗（可能是因為 version_md5 欄位不存在），嘗試不包含該欄位的查詢
        result = await supabaseRequest(
          'dictionary_versions?select=version,updated_at&order=updated_at.desc&limit=1'
        );
      } else {
        // 其他錯誤，重新拋出
        throw error;
      }
    }

    if (result && result.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Supabase API] 成功獲取版本號:', result[0].version);
      }
      return {
        version: result[0].version,
        versionMD5: result[0].version_md5 || null,
        updatedAt: result[0].updated_at
      };
    }

    // 如果結果為空數組，表示數據庫中沒有版本記錄
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Supabase API] 數據庫中沒有版本記錄');
    }
    return null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Supabase API] 獲取字典版本號失敗:', error);
      console.error('[Supabase API] 錯誤詳情:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
    }
    // 重新拋出錯誤，讓調用者可以處理
    throw error;
  }
};

/**
 * 獲取所有字典數據（結構化格式）
 * 返回的數據結構與本地字典文件相同，便於直接使用
 */
export const getAllDictionaries = async () => {
  if (!isConfigured()) {
    console.warn('Supabase not configured. Using local dictionaries.');
    return null;
  }

  try {
    // 並行獲取所有數據
    const [
      mtrData,
      lrtData,
      mtrBusData,
      mtrStatusData,
      trafficNewsData,
      immigrationData
    ] = await Promise.all([
      getMTRDictionary(),
      getLRTDictionary(),
      getMTRBusDictionary(),
      getMTRStatusDictionary(),
      getTrafficNewsDictionary(),
      getImmigrationDictionary()
    ]);

    return {
      MTR: mtrData,
      LRT: lrtData,
      MTRBus: mtrBusData,
      MTRStatus: mtrStatusData,
      TrafficNews: trafficNewsData,
      Immigration: immigrationData
    };
  } catch (error) {
    console.error('Failed to fetch dictionaries:', error);
    throw error;
  }
};

/**
 * 獲取 MTR 字典
 */
export const getMTRDictionary = async () => {
  if (!isConfigured()) {
    return null;
  }

  try {
    // 獲取翻譯
    const translations = await supabaseRequest(
      "translations?dictionary_type=eq.MTR&select=key,category,tc_name,en_name,metadata"
    );

    // 獲取路線
    const lines = await supabaseRequest(
      "mtr_lines?active=eq.true&select=line_code,tc_name,en_name,color_code,station_codes&order=display_order"
    );

    // 獲取站點
    const stations = await supabaseRequest(
      "mtr_stations?active=eq.true&select=station_code,tc_name,en_name,latitude,longitude,inter_change_lines,connected_stations,active"
    );

    // 構建字典結構
    const Common = {};
    const Error = {};
    const MtrLines = {};
    const MtrStations = {};
    const StationFacilities = {};

    // 處理翻譯
    translations.forEach(trans => {
      const segments = String(trans.key || '').split('.');
      const category = segments[0] || '';
      const key = segments.slice(1).join('.');
      if (category === 'common') {
        if (!Common.tc) Common.tc = {};
        if (!Common.en) Common.en = {};
        if (trans.tc_name) Common.tc[key] = trans.tc_name;
        if (trans.en_name) Common.en[key] = trans.en_name;
      } else if (category === 'error') {
        if (!Error.tc) Error.tc = {};
        if (!Error.en) Error.en = {};
        if (trans.tc_name) Error.tc[key] = trans.tc_name;
        if (trans.en_name) Error.en[key] = trans.en_name;
      } else if (category === 'stationFacility' && key) {
        StationFacilities[key.toUpperCase()] = trans.metadata || null;
      }
    });

    // 處理路線
    lines.forEach(line => {
      MtrLines[line.line_code] = {
        tc_name: line.tc_name,
        en_name: line.en_name,
        colorCode: line.color_code,
        stations: line.station_codes || []
      };
    });

    // 處理站點
    stations.forEach(station => {
      const stationCode = station.station_code;
      const stationFacilityData = StationFacilities[String(stationCode || '').toUpperCase()] || null;
      const latitude = station.latitude !== null && station.latitude !== undefined
        ? Number(station.latitude)
        : null;
      const longitude = station.longitude !== null && station.longitude !== undefined
        ? Number(station.longitude)
        : null;

      MtrStations[station.station_code] = {
        tc_name: station.tc_name,
        en_name: station.en_name,
        // `lat/lon` 與本地字典格式對齊；保留 `lag` 兼容舊程式碼
        lat: Number.isFinite(latitude) ? latitude : '',
        lag: Number.isFinite(latitude) ? latitude : '',
        lon: Number.isFinite(longitude) ? longitude : '',
        interChange: station.inter_change_lines || [],
        connectedStations: station.connected_stations || [],
        active: station.active ? '1' : '0',
        stationFacilities: stationFacilityData
      };
    });

    return { Common, Error, MtrLines, MtrStations, StationFacilities };
  } catch (error) {
    console.error('Failed to fetch MTR dictionary:', error);
    throw error;
  }
};

/**
 * 獲取 LRT 字典
 */
export const getLRTDictionary = async () => {
  if (!isConfigured()) {
    return null;
  }

  try {
    // 獲取翻譯
    const translations = await supabaseRequest(
      "translations?dictionary_type=eq.LRT&select=key,category,tc_name,en_name"
    );

    // 獲取站點
    const stations = await supabaseRequest(
      "lrt_stations?active=eq.true&select=station_id,tc_name,en_name,latitude,longitude,zone,active"
    );

    // 獲取路線
    const routes = await supabaseRequest(
      "lrt_routes?active=eq.true&select=route_code,tc_name,en_name,color_code,station_ids,route_type"
    );

    // 獲取收費區
    const zones = await supabaseRequest(
      "lrt_zones?select=zone_code,tc_name,en_name,station_ids"
    );

    // 構建字典結構
    const lrtCommon = {};
    const lrtStations = {};
    const lrtRoutes = {};
    const lrtZoneNames = {};
    const lrtZoneStation = {};

    // 處理翻譯
    translations.forEach(trans => {
      const [category, key] = trans.key.split('.');
      if (category === 'common') {
        if (!lrtCommon.tc) lrtCommon.tc = {};
        if (!lrtCommon.en) lrtCommon.en = {};
        if (trans.tc_name) lrtCommon.tc[key] = trans.tc_name;
        if (trans.en_name) lrtCommon.en[key] = trans.en_name;
      }
    });

    // 處理站點
    stations.forEach(station => {
      lrtStations[station.station_id] = {
        tc_name: station.tc_name,
        en_name: station.en_name,
        lag: station.latitude ? station.latitude.toString() : '',
        lon: station.longitude ? station.longitude.toString() : '',
        zone: station.zone || '',
        active: station.active ? '1' : '0'
      };
    });

    // 處理路線
    routes.forEach(route => {
      lrtRoutes[route.route_code] = {
        route_name: route.tc_name || '',
        route_rmk: '',
        colorCode: route.color_code || null,
        stations: route.station_ids || [],
        route_type: route.route_type || 'normal'
      };
    });

    // 處理收費區
    zones.forEach(zone => {
      lrtZoneNames[zone.zone_code] = {
        tc_name: zone.tc_name,
        en_name: zone.en_name
      };
      lrtZoneStation[zone.zone_code] = zone.station_ids || [];
    });

    return { lrtCommon, lrtStations, lrtRoutes, lrtZoneNames, lrtZoneStation };
  } catch (error) {
    console.error('Failed to fetch LRT dictionary:', error);
    throw error;
  }
};

/**
 * 獲取 MTRBus 字典
 */
export const getMTRBusDictionary = async () => {
  if (!isConfigured()) {
    return null;
  }

  try {
    // 獲取翻譯
    const translations = await supabaseRequest(
      "translations?dictionary_type=eq.MTRBus&select=key,category,tc_name,en_name"
    );

    // 獲取巴士
    const buses = await supabaseRequest(
      "mtr_buses?active=eq.true&select=fleet_number,plate_number,bus_type"
    );

    // 獲取路線
    const routes = await supabaseRequest(
      "mtr_bus_routes?active=eq.true&select=route_code,tc_name,en_name,direction,stop_codes,destination_code"
    );

    // 獲取站點（優先使用 v2 schema，若不存在則回退舊表）
    let stops = [];
    let useV2Stops = true;
    try {
      stops = await supabaseRequest(
        "mtr_bus_stops_v2?active=eq.true&select=stop_id,station_name_chi,station_name_eng,latitude,longitude,connections"
      );
    } catch (error) {
      useV2Stops = false;
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Supabase API] mtr_bus_stops_v2 不可用，回退至 mtr_bus_stops');
      }
      stops = await supabaseRequest(
        "mtr_bus_stops?active=eq.true&select=stop_code,tc_name,en_name,latitude,longitude,connections"
      );
    }

    // 獲取目的地
    const destinations = await supabaseRequest(
      "mtr_bus_destinations?select=dest_code,tc_name,en_name"
    );

    // 構建字典結構
    const common = {};
    const busesDict = {};
    const route = {};
    const stopsDict = {};
    const dest = {};

    // 處理翻譯
    translations.forEach(trans => {
      const [category, key] = trans.key.split('.');
      if (category === 'common') {
        common[key] = {
          tc_name: trans.tc_name || '',
          en_name: trans.en_name || ''
        };
      }
    });

    // 處理巴士
    buses.forEach(bus => {
      busesDict[bus.fleet_number] = {
        plateNo: bus.plate_number || '',
        type: bus.bus_type || ''
      };
    });

    // 處理路線（需要按路線代碼分組）
    const routeMap = {};
    routes.forEach(routeItem => {
      const [routeCode, direction] = routeItem.route_code.split('-');
      if (!routeMap[routeCode]) {
        routeMap[routeCode] = {
          tc_name: routeItem.tc_name || '',
          en_name: routeItem.en_name || '',
          bound: {}
        };
      }
      if (!routeMap[routeCode].bound[direction]) {
        routeMap[routeCode].bound[direction] = {
          dest: routeItem.destination_code || '',
          stops: routeItem.stop_codes || []
        };
      }
    });
    Object.keys(routeMap).forEach(code => {
      route[code] = routeMap[code];
    });

    // 處理站點
    stops.forEach(stop => {
      const stopCode = useV2Stops ? stop.stop_id : stop.stop_code;
      if (!stopCode) return;

      stopsDict[stopCode] = {
        tc_name: useV2Stops ? stop.station_name_chi : stop.tc_name,
        en_name: useV2Stops ? stop.station_name_eng : stop.en_name,
        lat: stop.latitude ? stop.latitude.toString() : '',
        lon: stop.longitude ? stop.longitude.toString() : '',
        connections: stop.connections || null
      };
    });

    // 處理目的地
    destinations.forEach(destItem => {
      dest[destItem.dest_code] = {
        tc_name: destItem.tc_name,
        en_name: destItem.en_name
      };
    });

    return { common, buses: busesDict, route, stops: stopsDict, dest };
  } catch (error) {
    console.error('Failed to fetch MTRBus dictionary:', error);
    throw error;
  }
};

/**
 * 獲取 MTRStatus 字典
 */
export const getMTRStatusDictionary = async () => {
  if (!isConfigured()) {
    return null;
  }

  try {
    const statusConfigs = await supabaseRequest(
      "status_configs?config_type=eq.mtr_status&select=status_key,tc_name,en_name,color,description_tc,description_en"
    );

    const StatusLight = {};
    statusConfigs.forEach(config => {
      StatusLight[config.status_key] = {
        tc_name: config.tc_name || '',
        en_name: config.en_name || '',
        color: config.color || null,
        tc_rmk: config.description_tc || null,
        en_rmk: config.description_en || null
      };
    });

    return { StatusLight };
  } catch (error) {
    console.error('Failed to fetch MTRStatus dictionary:', error);
    throw error;
  }
};

/**
 * 獲取 TrafficNews 字典
 */
export const getTrafficNewsDictionary = async () => {
  if (!isConfigured()) {
    return null;
  }

  try {
    // 獲取翻譯
    const translations = await supabaseRequest(
      "translations?dictionary_type=eq.TrafficNews&select=key,category,tc_name,en_name"
    );

    // 獲取消息類型
    const messageTypes = await supabaseRequest(
      "status_configs?config_type=eq.traffic_news&select=status_key,tc_name,en_name,color,icon"
    );

    const common = {};
    const messageTypesDict = {};

    // 處理翻譯
    translations.forEach(trans => {
      const [category, key] = trans.key.split('.');
      if (category === 'common') {
        common[key] = {
          tc_name: trans.tc_name || '',
          en_name: trans.en_name || ''
        };
      }
    });

    // 處理消息類型
    messageTypes.forEach(type => {
      messageTypesDict[type.status_key] = {
        tc_name: type.tc_name || '',
        en_name: type.en_name || '',
        icon: type.icon || null,
        color: type.color || null
      };
    });

    return { common, messageTypes: messageTypesDict };
  } catch (error) {
    console.error('Failed to fetch TrafficNews dictionary:', error);
    throw error;
  }
};

/**
 * 獲取 Immigration 字典
 */
export const getImmigrationDictionary = async () => {
  if (!isConfigured()) {
    return null;
  }

  try {
    // 獲取翻譯
    const translations = await supabaseRequest(
      "translations?dictionary_type=eq.Immigration&select=key,category,tc_name,en_name"
    );

    // 獲取管制站
    const controlPoints = await supabaseRequest(
      "immigration_control_points?active=eq.true&select=control_point_code,tc_name,en_name"
    );

    // 獲取狀態配置
    const waitingStatus = await supabaseRequest(
      "status_configs?config_type=eq.immigration&select=status_key,tc_name,en_name,color"
    );

    const common = {};
    const controlPointsDict = {};
    const waitingStatusDict = {};

    // 處理翻譯
    translations.forEach(trans => {
      const [category, key] = trans.key.split('.');
      if (category === 'common') {
        common[key] = {
          tc_name: trans.tc_name || '',
          en_name: trans.en_name || ''
        };
      }
    });

    // 處理管制站
    controlPoints.forEach(cp => {
      controlPointsDict[cp.control_point_code] = {
        tc_name: cp.tc_name,
        en_name: cp.en_name
      };
    });

    // 處理狀態
    waitingStatus.forEach(status => {
      waitingStatusDict[status.status_key] = {
        tc_name: status.tc_name || '',
        en_name: status.en_name || '',
        color: status.color || null
      };
    });

    return { common, controlPoints: controlPointsDict, waitingStatus: waitingStatusDict };
  } catch (error) {
    console.error('Failed to fetch Immigration dictionary:', error);
    throw error;
  }
};

/**
 * 獲取特定字典（兼容舊 API）
 */
export const getDictionary = async (dictName) => {
  if (!isConfigured()) {
    return null;
  }

  try {
    const dictionaries = await getAllDictionaries();
    return dictionaries[dictName] || null;
  } catch (error) {
    console.error(`Failed to fetch dictionary ${dictName}:`, error);
    throw error;
  }
};
